/* Use of `any` in this file is necessary for TypeScript to infer types based on usage. */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { decorate } from 'inversify';
import {
  defineStore, DefineStoreOptions, StateTree, Store, StoreActions, StoreGetters,
} from 'pinia';

import '@/pinia';
import { service } from '@/decorators/service';

type Newable = new() => any;

type StoreOptions = DefineStoreOptions<string, any, any, any>;

export const PiniaStore = (function () {}) as unknown as new() => Store;

const objectProperties = new RegExp(Object.getOwnPropertyNames(Object.prototype).join('|'));

function buildPiniaStoreOptions<T extends Newable, SS extends StoreOptions>(target: T, options?: SS) {
  const state = <StateTree> {};
  const actions = <StoreActions<SS>> {};
  const getters = <StoreGetters<SS>> {};
  const id = options?.id ?? target.name;

  // In order to determine a class' properties (for state), it must be instantiated.
  // eslint-disable-next-line new-cap
  const data = (new target()) as object;
  for (const [key, value] of Object.entries(data)) {
    state[key] = value as typeof data[keyof typeof data];
  }

  let prototype = Object.getPrototypeOf(data) as object;
  while (prototype) {
    const keys = Object.getOwnPropertyNames(prototype);
    for (let i = 0, l = keys.length; i < l; i += 1) {
      const key = keys[i];

      // Ignore Object properties.
      if (!objectProperties.test(key)) {
        const descriptor = Object.getOwnPropertyDescriptor(prototype, key)!;

        if (descriptor.value !== undefined) {
          // methods
          if (typeof descriptor.value === 'function') {
            actions[key as keyof typeof actions] = descriptor.value as typeof actions[keyof typeof actions];
          }
          else {
            // typescript decorated data
            state[key as keyof typeof state] = descriptor.value as object;
          }
        }
        else if (descriptor.get) {
          // computed properties
          // eslint-disable-next-line @typescript-eslint/unbound-method
          getters[key as keyof typeof getters] = descriptor.get as typeof getters[keyof typeof getters];
        }
      }
    }

    prototype = Object.getPrototypeOf(prototype) as object;
  }

  return {
    id,
    state: () => state,
    actions,
    getters,
  };
}

export function store(options?: StoreOptions) {
  return function storeDecorator <TTarget extends Newable>(target: TTarget) {
    const storeOptions = buildPiniaStoreOptions(target, options);
    const instance = defineStore(storeOptions.id, storeOptions);

    Object.setPrototypeOf(target, instance);

    decorate(service(target, 'Singleton', true), target);
  };
}
